import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { useSelector } from 'react-redux';
import _ from 'underscore';

import { useAppContext } from 'hooks/useContext';
import { Languages } from 'constants/languages';
import { getCookie } from 'functions/getCookie';
import { Button, OutlineButton } from 'components/Button/RoundButton';
import StepProgressBar from './StepProgressBar';
import getComputedDescription from 'functions/getComputedDescription';
import {
  FlowText,
  FullScreenModal,
  FullScreenHeaderContainer,
  HeaderComponent,
  FullScreenModalTitle,
  FullScreenModalBody,
  FullScreenFooterContainer,
  LanguageContainer,
  TextContainer,
  LevelDataContainer,
} from './styles';
import { codingButtonParser, interactParser, quizParser } from './Parser';

export default React.memo(
  ({ courseFlow, updateFlowProgress, inputExercise, close, animationAnchorRef, isCreator = false }) => {
    const navigate = useNavigate();

    const closeRef = useRef(close);
    const updateFlowProgressRef = useRef(updateFlowProgress);

    const { levelIndexStart, courseId, testImpl, language } = courseFlow;

    const parsedFlow = testImpl.parsedFlow;
    const levelNames = testImpl.generated.allLevelNames;
    const exerciseId = testImpl.test;

    const { language: appLanguage } = useAppContext();

    let samePageCallRef = useRef(false);
    let modalRef = useRef(null);

    const { user } = useSelector((state) => state.auth);

    const [userLevel, setUserLevel] = useState(levelIndexStart);
    const [subLevel, setSubLevel] = useState(0);
    const [subLevelsData, setSubLevelsData] = useState([]);
    const [hasCompletedInteraction, setHasCompletedInteraction] = useState(true);

    useEffect(() => localStorage.setItem('fsCourse-flow-opened-on-pathname', window.location.pathname), []);

    const closeWithAnimation = useCallback(() => {
      if (window.location.pathname.includes('course') || !animationAnchorRef?.current || isCreator)
        return closeRef.current();

      const modal = modalRef.current;
      const animationAnchor = animationAnchorRef.current;

      const modalRect = modal.getBoundingClientRect();
      const anchorRect = animationAnchor.getBoundingClientRect();

      // Calculate the position for the animation
      const translateX = anchorRect.left - modalRect.left + anchorRect.width / 2 - modalRect.width / 2;
      const translateY = anchorRect.top - modalRect.top + anchorRect.height / 2 - modalRect.height / 2;

      // Create a GSAP timeline for the animation
      const GSAPTimeline = gsap.timeline();
      GSAPTimeline.to(modal, {
        duration: 0.5,
        scale: 0.3,
        x: translateX,
        y: translateY,
        opacity: 1,
      });
      GSAPTimeline.to(modal, { duration: 0.5, scale: 0.1, onComplete: () => closeRef.current(userLevel) });
      GSAPTimeline.play();
    }, [isCreator, animationAnchorRef, userLevel]);

    const parseCodingButton = useCallback(
      (text) => codingButtonParser(text, exerciseId, courseId, isCreator, navigate, closeWithAnimation),
      [exerciseId, courseId, isCreator, closeWithAnimation, navigate]
    );

    const parseQuiz = useCallback(
      (text, subLevelIndex) => {
        return quizParser(
          text,
          subLevelIndex,
          userLevel,
          exerciseId,
          isCreator,
          setHasCompletedInteraction,
          user?._id,
          testImpl._id,
          language,
          updateFlowProgressRef.current,
          inputExercise
        );
      },
      [userLevel, exerciseId, isCreator, user, testImpl._id, language, setHasCompletedInteraction, inputExercise]
    );

    const parseInteract = useCallback(
      (text, subLevelIndex, currentLevelIndex) =>
        interactParser(
          text,
          subLevelIndex,
          currentLevelIndex,
          userLevel,
          exerciseId,
          isCreator,
          testImpl,
          user?._id,
          language,
          updateFlowProgressRef.current,
          inputExercise,
          setHasCompletedInteraction
        ),
      [userLevel, isCreator, testImpl, user, exerciseId, language, inputExercise, setHasCompletedInteraction]
    );

    const getLanguage = useCallback(
      () =>
        (courseId
          ? getCookie(`fsCourse-${courseId}-language`)
          : localStorage.getItem(`fsEditor-exercise-${exerciseId}-language`)) ?? 'python',
      [courseId, exerciseId]
    );

    const parseSubLevelData = useCallback(
      (data, subLevelIndex, currentLevelIndex) => {
        data = getComputedDescription(data, 'exercise', getLanguage(), userLevel, appLanguage, true, true);
        if (data.includes('[coding]')) return parseCodingButton(data);
        if (data.includes('[quiz]')) return parseQuiz(data, subLevelIndex);
        if (data.includes('[interact-')) return parseInteract(data, subLevelIndex, currentLevelIndex);
        return { data, type: 'html' };
      },
      [parseCodingButton, parseQuiz, parseInteract, getLanguage, userLevel, appLanguage]
    );

    const inputExerciseFlowProgress = useMemo(() => {
      return inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress
        ? JSON.parse(JSON.stringify(inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress))
        : [];
    }, [inputExercise?.levelData, language, userLevel]);

    useEffect(() => {
      if (inputExerciseFlowProgress.length) {
        let defaultData = inputExerciseFlowProgress.map(
          (_, index) =>
            parseSubLevelData(parsedFlow[userLevel][index], index, inputExerciseFlowProgress.length - 1).data
        );
        setSubLevelsData(defaultData);
        setSubLevel(defaultData.length - 1);
        if (typeof defaultData[defaultData.length - 1] === 'string') setHasCompletedInteraction(true);
      } else {
        let parsedData = parseSubLevelData(parsedFlow[userLevel][0], 0, 0);
        setSubLevelsData([parsedData.data]);
        setSubLevel(0);
        if (parsedData.type === 'html') setHasCompletedInteraction(true);
        let dataToBeStore = [parsedData.data];
        if (parsedData.type !== 'html') {
          dataToBeStore = [
            {
              type: parsedData.type,
            },
          ];
        }
        if (!isCreator) {
          updateFlowProgressRef.current({
            test: exerciseId,
            language: language,
            level: userLevel,
            data: dataToBeStore,
          });
        }
      }
      localStorage.setItem(`fsEditor-exercise-${exerciseId}-levelToRun`, String(userLevel));
    }, [
      exerciseId,
      parsedFlow,
      userLevel,
      isCreator,
      testImpl._id,
      language,
      parseSubLevelData,
      inputExerciseFlowProgress,
    ]);

    const executeScroll = (index) => {
      let element = document.getElementById(`sub-level-${index}`);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
      samePageCallRef.current = false;
    };

    useEffect(() => {
      if (!subLevelsData.length) return;
      let modalBodyHeight = document.getElementById(`body`)?.clientHeight;
      subLevelsData.forEach((_, index) => {
        let currentElement = document.getElementById(`sub-level-${index}`);
        currentElement.style.paddingTop = '20px';
        currentElement.style.paddingBottom = '10px';
        let currentSubLevelParentElement = currentElement.parentNode;
        if (subLevel === index) {
          currentSubLevelParentElement.style.minHeight = `${modalBodyHeight}px`;
        } else {
          currentSubLevelParentElement.style.minHeight = '0px';
        }
      });
      setTimeout(() => executeScroll(samePageCallRef.current ? 0 : subLevel), 100);
    }, [subLevelsData, subLevel]);

    const handleContinueButton = () => {
      let newSubLevel = subLevel + 1;
      let parsedData = parseSubLevelData(parsedFlow?.[userLevel]?.[newSubLevel], newSubLevel, newSubLevel);
      let newSubLevelData = [...subLevelsData, parsedData.data];
      setSubLevel(newSubLevel);
      setSubLevelsData(newSubLevelData);
      let dataToBeSaved;
      if (parsedData.type === 'html') {
        dataToBeSaved = [...inputExerciseFlowProgress, parsedData.data];
      } else {
        dataToBeSaved = [
          ...inputExerciseFlowProgress,
          {
            type: parsedData.type,
          },
        ];
      }
      if (!isCreator) {
        updateFlowProgressRef.current({
          test: exerciseId,
          language: language,
          level: userLevel,
          data: dataToBeSaved,
        });
      }
      executeScroll(newSubLevel);
    };

    const renderButton = (subLevelIndex) => {
      if (parsedFlow[userLevel][subLevelIndex]?.includes('[coding]')) return;
      if (subLevelIndex === parsedFlow[userLevel]?.length - 1 && hasCompletedInteraction) {
        const isLastLevel = _.size(parsedFlow) - 1 === userLevel && parsedFlow[userLevel].length - 1 === subLevelIndex;
        return (
          <Button
            primaryBlue
            primaryGreenBorder
            small
            margin="0 0 auto"
            onClick={() => {
              // It check whether it is the last sub level of last level
              if (isLastLevel) {
                if (courseId && window.location.pathname.includes('exercise')) return navigate(`/courses/${courseId}`);
                if (!courseId && window.location.pathname.includes('exercise')) return navigate(`/exercises`);
                return closeRef.current();
              }
              setUserLevel((level) => level + 1);
            }}
          >
            {isLastLevel ? 'Proceed to new exercise' : 'Proceed to next level'}
          </Button>
        );
      }
      if (subLevelIndex === subLevel && hasCompletedInteraction)
        return (
          <Button primaryBlue primaryGreenBorder small margin="0 0 auto" onClick={() => handleContinueButton()}>
            Continue
          </Button>
        );
    };

    const checkCurrentLevelHasCodingButton = () => {
      let hasCodingButton = false;
      parsedFlow[userLevel].forEach((level) => {
        if (level.includes('[coding]')) hasCodingButton = true;
      });
      return hasCodingButton;
    };

    return (
      <FullScreenModal isOpen={true} innerRef={modalRef}>
        <FullScreenHeaderContainer>
          <FullScreenModalTitle>
            <HeaderComponent className="left">{levelNames[userLevel]}</HeaderComponent>
            <StepProgressBar
              levelsData={parsedFlow}
              currentLevel={userLevel}
              language={language}
              inputExercise={inputExercise}
              setCurrentLevel={(level) => {
                samePageCallRef.current = true;
                setUserLevel(level);
              }}
            />
            <HeaderComponent className="right">
              {checkCurrentLevelHasCodingButton() && (
                <OutlineButton
                  small
                  secondaryBlue
                  margin="0 0 auto"
                  onClick={() => {
                    if (isCreator) return closeRef.current();
                    if (courseId) navigate(`/exercise/${exerciseId}?courseId=${courseId}`);
                    closeWithAnimation();
                  }}
                >
                  Continue Coding
                </OutlineButton>
              )}
            </HeaderComponent>
          </FullScreenModalTitle>
        </FullScreenHeaderContainer>
        <FullScreenModalBody id="body">
          <LevelDataContainer>
            {subLevelsData.map((data, subLevelIndex) => {
              return (
                <TextContainer key={`subFlow${userLevel}${subLevelIndex}`}>
                  {typeof data === 'string' ? (
                    <FlowText id={`sub-level-${subLevelIndex}`} dangerouslySetInnerHTML={{ __html: data }} />
                  ) : (
                    <FlowText id={`sub-level-${subLevelIndex}`}>
                      {data?.map((element, i) => (
                        <React.Fragment key={`sub-level-${subLevelIndex}-${userLevel}-${i}`}>{element}</React.Fragment>
                      ))}
                    </FlowText>
                  )}
                  {renderButton(subLevelIndex)}
                </TextContainer>
              );
            })}
          </LevelDataContainer>
        </FullScreenModalBody>
        <FullScreenFooterContainer>
          <LanguageContainer>Language: {Languages[language].SimpleName}</LanguageContainer>
          <OutlineButton small secondaryBlue removeMargin onClick={() => closeWithAnimation()}>
            Close
          </OutlineButton>
        </FullScreenFooterContainer>
      </FullScreenModal>
    );
  }
);
